import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Users, Plus, Edit2, Trash2, Award, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EOSAccountabilityChart = () => {
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [seatDialogOpen, setSeatDialogOpen] = useState(false);
  const [editingSeat, setEditingSeat] = useState(null);

  // Seat form state
  const [seatForm, setSeatForm] = useState({
    title: '',
    person_user_id: '',
    five_roles: ['', '', '', '', ''],
    department: '',
    level: 0
  });

  useEffect(() => {
    fetchChart();
  }, []);

  const fetchChart = async () => {
    try {
      const response = await axios.get(`${API}/eos/accountability-chart`);
      setChart(response.data);
    } catch (error) {
      console.error('Error fetching accountability chart:', error);
      toast.error('Failed to load accountability chart');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSeat = () => {
    setEditingSeat(null);
    setSeatForm({
      title: '',
      person_user_id: '',
      five_roles: ['', '', '', '', ''],
      department: '',
      level: 0
    });
    setSeatDialogOpen(true);
  };

  const handleEditSeat = (seat) => {
    setEditingSeat(seat);
    setSeatForm({
      title: seat.title,
      person_user_id: seat.person_user_id || '',
      five_roles: seat.five_roles.length === 5 ? seat.five_roles : ['', '', '', '', ''],
      department: seat.department || '',
      level: seat.level || 0
    });
    setSeatDialogOpen(true);
  };

  const handleSaveSeat = async () => {
    try {
      if (!seatForm.title.trim()) {
        toast.error('Please enter a seat title');
        return;
      }

      const filteredRoles = seatForm.five_roles.filter(role => role.trim() !== '');
      
      const newSeat = {
        id: editingSeat?.id || `seat_${Date.now()}`,
        title: seatForm.title,
        person_user_id: seatForm.person_user_id || null,
        five_roles: filteredRoles,
        department: seatForm.department,
        level: parseInt(seatForm.level) || 0,
        key_metrics: editingSeat?.key_metrics || [],
        gwc_score: editingSeat?.gwc_score || null
      };

      let updatedSeats;
      if (editingSeat) {
        updatedSeats = chart.seats.map(s => s.id === editingSeat.id ? newSeat : s);
      } else {
        updatedSeats = [...chart.seats, newSeat];
      }

      await axios.post(`${API}/eos/accountability-chart`, { seats: updatedSeats });
      
      toast.success(editingSeat ? 'Seat updated' : 'Seat added');
      setSeatDialogOpen(false);
      fetchChart();
    } catch (error) {
      console.error('Error saving seat:', error);
      toast.error('Failed to save seat');
    }
  };

  const handleDeleteSeat = async (seatId) => {
    if (!window.confirm('Are you sure you want to delete this seat?')) {
      return;
    }

    try {
      const updatedSeats = chart.seats.filter(s => s.id !== seatId);
      await axios.post(`${API}/eos/accountability-chart`, { seats: updatedSeats });
      toast.success('Seat deleted');
      fetchChart();
    } catch (error) {
      console.error('Error deleting seat:', error);
      toast.error('Failed to delete seat');
    }
  };

  const groupSeatsByLevel = () => {
    if (!chart?.seats) return {};
    
    const grouped = {};
    chart.seats.forEach(seat => {
      const level = seat.level || 0;
      if (!grouped[level]) {
        grouped[level] = [];
      }
      grouped[level].push(seat);
    });
    
    return grouped;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading Accountability Chart...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const groupedSeats = groupSeatsByLevel();
  const levels = Object.keys(groupedSeats).sort((a, b) => parseInt(a) - parseInt(b));

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="h-8 w-8 text-purple-600" />
            <div>
              <h1 className="text-3xl font-bold">Accountability Chart</h1>
              <p className="text-sm text-muted-foreground">
                Define seats, roles, and responsibilities (not names)
              </p>
            </div>
          </div>
          <Button onClick={handleAddSeat}>
            <Plus className="h-4 w-4 mr-2" />
            Add Seat
          </Button>
        </div>

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950">
          <CardContent className="p-4">
            <p className="text-sm">
              💡 <strong>Tip:</strong> The Accountability Chart defines the structure of your organization 
              through "seats" rather than people. Each seat should have 5 key roles that capture the essence 
              of that position. People fill seats, but the chart itself is about the business structure.
            </p>
          </CardContent>
        </Card>

        {/* Chart Visualization */}
        {chart?.seats && chart.seats.length > 0 ? (
          <div className="space-y-6">
            {levels.map(level => (
              <div key={level}>
                <div className="mb-4">
                  <Badge variant="outline" className="text-lg">
                    Level {level} {level === '0' && '(Leadership)'}
                  </Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedSeats[level].map(seat => (
                    <Card key={seat.id} className="hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-lg">{seat.title}</CardTitle>
                            {seat.department && (
                              <Badge variant="outline" className="mt-2">
                                {seat.department}
                              </Badge>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditSeat(seat)}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteSeat(seat.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {/* Person */}
                        {seat.person_user_id && (
                          <div className="mb-3 p-2 rounded bg-muted">
                            <div className="flex items-center gap-2">
                              <Award className="h-4 w-4 text-blue-600" />
                              <span className="text-sm font-medium">Filled by User</span>
                            </div>
                          </div>
                        )}

                        {/* Five Roles */}
                        <div>
                          <div className="text-sm font-medium mb-2 flex items-center gap-2">
                            <TrendingUp className="h-4 w-4" />
                            5 Key Roles:
                          </div>
                          {seat.five_roles && seat.five_roles.length > 0 ? (
                            <ul className="space-y-1">
                              {seat.five_roles.map((role, idx) => (
                                <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                                  <span className="text-blue-600">•</span>
                                  {role}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-muted-foreground italic">No roles defined yet</p>
                          )}
                        </div>

                        {/* Metrics */}
                        {seat.key_metrics && seat.key_metrics.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <div className="text-sm font-medium mb-1">Key Metrics:</div>
                            <div className="flex flex-wrap gap-1">
                              {seat.key_metrics.map((metric, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {metric}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="text-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                No seats defined yet. Start building your accountability chart!
              </p>
              <Button onClick={handleAddSeat}>
                <Plus className="h-4 w-4 mr-2" />
                Add First Seat
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Seat Dialog */}
        <Dialog open={seatDialogOpen} onOpenChange={setSeatDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingSeat ? 'Edit Seat' : 'Add New Seat'}</DialogTitle>
              <DialogDescription>
                Define the seat title, department, and 5 key roles
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 max-h-96 overflow-y-auto">
              <div>
                <Label>Seat Title *</Label>
                <Input
                  placeholder="e.g., Visionary, Integrator, Sales Manager"
                  value={seatForm.title}
                  onChange={(e) => setSeatForm({ ...seatForm, title: e.target.value })}
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Department</Label>
                <Input
                  placeholder="e.g., Leadership, Sales, Operations"
                  value={seatForm.department}
                  onChange={(e) => setSeatForm({ ...seatForm, department: e.target.value })}
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Organization Level (0 = Leadership)</Label>
                <Input
                  type="number"
                  min="0"
                  value={seatForm.level}
                  onChange={(e) => setSeatForm({ ...seatForm, level: e.target.value })}
                  className="mt-2"
                />
              </div>

              <div>
                <Label>5 Key Roles</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Define the 5 most important responsibilities for this seat
                </p>
                {seatForm.five_roles.map((role, idx) => (
                  <Input
                    key={idx}
                    placeholder={`Role ${idx + 1}`}
                    value={role}
                    onChange={(e) => {
                      const newRoles = [...seatForm.five_roles];
                      newRoles[idx] = e.target.value;
                      setSeatForm({ ...seatForm, five_roles: newRoles });
                    }}
                    className="mt-2"
                  />
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setSeatDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveSeat}>
                {editingSeat ? 'Update Seat' : 'Add Seat'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default EOSAccountabilityChart;
